import axios, {
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosError,
  AxiosHeaders,
} from 'axios';
import { createApiHelpers } from '@tools/conns/Conns.tsx';
import { logInfo } from '@tools/LogsHandler.tsx';
import { goTo } from '@tools/nav/Navigate.tsx';
import { getFormUrl } from '@tools/conns/PathHandler.tsx';

// ──────────────────────────────────────────────────────────
//  1. Normaliza la base URL SIN barra final
// ──────────────────────────────────────────────────────────
// const BASE_URL = (import.meta.env.VITE_API_URL ?? 'http://127.0.0.1:8000').replace(/\/+$/, '');
// const BASE_URL = (import.meta.env.VITE_API_URL ?? 'https://17dfb889fc50.ngrok-free.app').replace(/\/+$/, '');

/* Base URL same-origin vía proxy:
 * - VITE_API_URL: host completo (https://api.example.com)
 * - VITE_API_BASE: prefijo relativo (solo host/path) — opcional
 * - fallback → en tests apunta al mock (http://127.0.0.1:8000) y en runtime usa '/'
 */
const BASE_URL = (() => {
  if (import.meta.env.MODE === 'test') {
    logInfo('AxiosConfig - BASE_URL (test): ', 'http://localhost:8000');
    return 'http://localhost:8000';
  }

  const env = import.meta.env as Record<string, unknown> | undefined;
  const raw = (env?.VITE_API_URL as string | undefined) ?? (env?.VITE_API_BASE as string | undefined);
  const fallback = '/';

  const normalized = typeof raw === 'string' && raw.trim() ? raw.trim() : fallback;

  if (/^https?:\/\//i.test(normalized)) {
    return normalized.replace(/\/+$/, '');
  }

  if (normalized === '/' || normalized === '') {
    return '/';
  }

  if (normalized.startsWith('/')) {
    return normalized.replace(/\/+$/, '');
  }

  if (/^https?:\/\//i.test(fallback)) {
    return fallback.replace(/\/+$/, '');
  }

  return `/${normalized.replace(/\/+$/, '')}`;
})();

if (import.meta.env.MODE !== 'test') {
  logInfo('AxiosConfig - BASE_URL: ', BASE_URL);
}

// Habilita `withCredentials` globalmente
axios.defaults.withCredentials = true;

/* ──────────────────────────────────────────────────────────
 *  2) Helpers para destino post-login (cuando redirige el interceptor)
 * ────────────────────────────────────────────────────────── */
const AUTH_PREFIX = '/auth';
const AUTH_BLOCKLIST = new Set<string>([
  '/auth/login',
  '/auth/logout',
  '/auth/register',
]);

const isSafeInternalPath = (raw?: string | null): string | undefined => {
  if (!raw) return undefined;
  // Evita open-redirects a dominios externos
  if (/^https?:\/\//i.test(raw)) return undefined;
  const norm = raw.replace(/^\/+/, '/');
  if (!norm.startsWith('/')) return undefined;
  // Evita redirigir a las rutas de auth para no entrar en bucles
  if (AUTH_BLOCKLIST.has(norm)) return undefined;
  return norm;
};

const rememberPostLogin = (to?: string, reason?: string) => {
  try {
    if (!to) return;
    sessionStorage.setItem(
      'postLoginRedirect',
      JSON.stringify({ to, reason, ts: Date.now() }),
    );
  } catch {
    /* noop */
  }
};

let isRedirecting = false;

export const resetAuthRedirectGuard = () => {
  isRedirecting = false;
};

/* ──────────────────────────────────────────────────────────
 *  3) Instancias Axios
 *     - baseAxiosInstance: con auth/interceptores
 *     - noAuthAxiosInstance: sin auth (público)
 * ────────────────────────────────────────────────────────── */
const createAxios = (baseURL: string): AxiosInstance =>
  axios.create({
    baseURL,
    withCredentials: true,
  });

export const baseAxiosInstance = createAxios(BASE_URL);
export const noAuthAxiosInstance = createAxios(BASE_URL);



/* ──────────────────────────────────────────────────────────
 *  4) Interceptor de request: limpia barras finales en URL
 *     (evita // en combinaciones baseURL + url)
 * ────────────────────────────────────────────────────────── */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const stripTrailingSlash = <T = any>(
  config: InternalAxiosRequestConfig<T>,
): InternalAxiosRequestConfig<T> => {
  if (typeof config.url === 'string') {
    config.url = config.url.replace(/\/+$/, '');
  }
  return config;
};

baseAxiosInstance.interceptors.request.use(stripTrailingSlash);
noAuthAxiosInstance.interceptors.request.use(stripTrailingSlash);

const dropFormDataContentType = <T = any>(
  config: InternalAxiosRequestConfig<T>,
): InternalAxiosRequestConfig<T> => {
  if (config.data instanceof FormData && config.headers) {
    const headers =
      config.headers instanceof AxiosHeaders
        ? config.headers
        : AxiosHeaders.from(config.headers);

    headers.delete('Content-Type');
    headers.delete('content-type');
    headers.setContentType(null);

    config.headers = headers;
  }
  return config;
};

baseAxiosInstance.interceptors.request.use(dropFormDataContentType);
noAuthAxiosInstance.interceptors.request.use(dropFormDataContentType);

/* ──────────────────────────────────────────────────────────
 *  5) Interceptor de respuesta (AUTH): captura 401/403
 *     - 401 → Intenta un token refresh silencioso:
 *         - Pausa las peticiones y las encola.
 *         - Si tiene éxito, relanza las peticiones pausadas.
 *         - Si el refresh falla, limpia sesión y redirige a login.
 *     - 403 → autenticado sin permiso: lo maneja la pantalla/componente
 * ────────────────────────────────────────────────────────── */
const USE_AUTH_INTERCEPTOR = true;

let isRefreshing = false;
let refreshSubscribers: ((error: Error | null) => void)[] = [];

const onRefreshed = (error: Error | null) => {
  refreshSubscribers.forEach(callback => callback(error));
  refreshSubscribers = [];
};

const addSubscriber = (callback: (error: Error | null) => void) => {
  refreshSubscribers.push(callback);
};

if (USE_AUTH_INTERCEPTOR) {
  baseAxiosInstance.interceptors.response.use(
    res => res,
    async (err: AxiosError) => {
      const status = err.response?.status;
      const originalRequest = err.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // Si nos redirigen cuando intentamos conectarnos a las rutas de auth (ej un refresh que falla), salimos de aquí.
      if (originalRequest.url?.includes('/auth/login') || originalRequest.url?.includes('/auth/refresh')) {
        return Promise.reject(err);
      }

      const forceLogout = async (reason: string) => {
        if (isRedirecting) return Promise.reject(err);
        isRedirecting = true;

        isRefreshing = false;

        // Path actual (pathname + search + hash)
        const current =
          (window.location?.pathname ?? '/') +
          (window.location?.search ?? '') +
          (window.location?.hash ?? '');

        const safe = isSafeInternalPath(current);

        // No recordar/loop si ya estamos dentro de /auth/*
        if (safe && !window.location.pathname.startsWith(AUTH_PREFIX)) {
          rememberPostLogin(safe, reason);
        }

        try {
          if (reason === 'unauthenticated') {
            const [{ store: lazyStore }, { logout: lazyLogout }] = await Promise.all([
              import('@store/store.tsx'),
              import('@store/slices/authSlice.tsx'),
            ]);
            await Promise.resolve(lazyStore.dispatch<any>(lazyLogout()));
          }
        } catch (error) {
          logInfo('Axios interceptor: Error during force logout redirect logic:', error);
        } finally {
          logInfo(
            `Axios interceptor: redirect to /auth/login?reason=${reason}`,
          );
          goTo(`/auth/login?reason=${reason}`, { replace: true });
        }

        return Promise.reject(err);
      }

      if (status === 401) {
        if (!originalRequest._retry) {
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              addSubscriber((refreshError) => {
                if (refreshError) {
                  reject(refreshError);
                } else {
                  originalRequest._retry = true;
                  resolve(baseAxiosInstance(originalRequest));
                }
              });
            });
          }

          // Original Request que dispara el proceso general de Refreshing
          originalRequest._retry = true;
          isRefreshing = true;

          try {
            logInfo('Axios interceptor: AccessToken expirado. Intentando refresh silencioso...');
            const refreshUrl = getFormUrl('auth:refresh');
            await noAuthAxiosInstance.post(refreshUrl);
            resetAuthRedirectGuard();
            logInfo('Axios interceptor: Sesión reanudada con éxito (refresh silencioso).');

            isRefreshing = false;
            onRefreshed(null);

            return baseAxiosInstance(originalRequest);
          } catch (refreshErr) {
            logInfo('Axios interceptor: Refresh silencioso falló. Cerrando sesión...');
            const error = new Error('Refresh token expiro o es invalido');
            onRefreshed(error);
            return await forceLogout('unauthenticated');
          }
        }

        // _retry es true pero falló el 401. Force Logout.
        return await forceLogout('unauthenticated');
      }

      if (status === 403) {
        return Promise.reject(err);
      }

      return Promise.reject(err);
    },
  );
}

/* ──────────────────────────────────────────────────────────
 *  6) Helpers de API generados (tipados), con y sin auth
 * ────────────────────────────────────────────────────────── */
const {
  conns: generatedConns,
  connsDirect: generatedConnsDirect,
} = createApiHelpers(baseAxiosInstance);

const {
  conns: _connsPublic,
  connsDirect: _connsDirectPublic,
} = createApiHelpers(noAuthAxiosInstance);

/* ──────────────────────────────────────────────────────────
 *  7) Exports
 * ────────────────────────────────────────────────────────── */
export const api = baseAxiosInstance; // usar directamente cuando necesites axios
export const conns = generatedConns; // helpers tipados (requieren estar logueado)
export const connsDirect = generatedConnsDirect;
export const connsPublic = _connsPublic;
export const connsDirectPublic = _connsDirectPublic;

type VitestMock = import('vitest').Mock;

export const __mocks = undefined as unknown as {
  getMock: VitestMock;
  postMock: VitestMock;
};
